import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useState } from "react";
import {
  addUserToGroup,
  createGroup,
  deleteGroup,
  getGroupById,
  removeUserFromGroup,
} from "../api/groups.api";
import { CreateGroupRow, GroupRow } from "../api/group.type";
import { useToastContext } from "../containers/ToastContextWrapper";
import { getErrorMessage } from "../utils/errors";
import { useContactsContext } from "./ContactsContextWrapper";
import { useLoadGroups } from "./useLoadGroups";

interface GroupsContextType {
  groups: GroupRow[];
  selectedGroup: GroupRow | null;
  addUser: (params: { groupId: string; userId: string }) => Promise<void>;
  removeUser: (params: { groupId: string; userId: string }) => Promise<void>;
  create: (group: CreateGroupRow) => Promise<GroupRow>;
  remove: (id: string) => Promise<void>;
  setGroupId: (groupId: string) => void;
  loading: boolean;
  addUserLoading: boolean;
  removeUserLoading: boolean;
}

export const GroupsContext = createContext<GroupsContextType>({
  groups: [],
  selectedGroup: null,
  addUser: async () => {},
  removeUser: async () => {},
  create: async () => ({ id: "" } as GroupRow),
  remove: async () => {},
  setGroupId: () => {},
  loading: false,
  addUserLoading: false,
  removeUserLoading: false,
});

export const GroupsContextWrapper = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { showToast } = useToastContext();
  const queryClient = useQueryClient();
  const { selectedContact } = useContactsContext();
  const selectedContactRecord = selectedContact?.contact;
  const selectedContactId = selectedContactRecord?.id;
  const selectedRelatedUserId = selectedContactRecord?.related_user_id;
  const [groupId, setGroupId] = useState<string | null>(null);
  const { groups, isLoading: isLoadingGroups } = useLoadGroups();

  const { data: selectedGroup } = useQuery({
    queryKey: ["group", groupId],
    queryFn: () => getGroupById(groupId || ""),
    enabled: !!groupId,
  });

  const { mutateAsync: create } = useMutation({
    mutationFn: createGroup,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
    onError(error) {
      showToast({ title: "Error", description: getErrorMessage(error) });
    },
  });

  const { mutateAsync: remove } = useMutation({
    mutationFn: deleteGroup,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
    },
    onError(error) {
      showToast({ title: "Error", description: getErrorMessage(error) });
    },
  });

  const { mutateAsync: addUser, isPending: addUserLoading } = useMutation({
    mutationFn: addUserToGroup,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["group-members"] });
      queryClient.invalidateQueries({ queryKey: ["selectedContact"] });
      if (selectedContactId) {
        queryClient.invalidateQueries({
          queryKey: ["user-groups", selectedContactId],
        });
      }
      if (selectedRelatedUserId) {
        queryClient.invalidateQueries({
          queryKey: ["user-groups", selectedRelatedUserId],
        });
      }
      showToast({
        title: "User added to group",
        description: "User added to group successfully",
      });
    },
    onError(error) {
      showToast({ title: "Error", description: getErrorMessage(error) });
    },
  });

  const { mutateAsync: removeUser, isPending: removeUserLoading } = useMutation({
    mutationFn: removeUserFromGroup,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["group-members"] });
      queryClient.invalidateQueries({ queryKey: ["selectedContact"] });
      if (selectedContactId) {
        queryClient.invalidateQueries({
          queryKey: ["user-groups", selectedContactId],
        });
      }
      if (selectedRelatedUserId) {
        queryClient.invalidateQueries({
          queryKey: ["user-groups", selectedRelatedUserId],
        });
      }
      showToast({
        title: "User removed from group",
        description: "User removed from group successfully",
      });
    },
    onError(error) {
      showToast({ title: "Error", description: getErrorMessage(error) });
    },
  });

  return (
    <GroupsContext.Provider
      value={{
        groups,
        selectedGroup: selectedGroup || null,
        addUser,
        removeUser,
        create,
        remove,
        setGroupId,
        loading: isLoadingGroups,
        addUserLoading,
        removeUserLoading,
      }}
    >
      {children}
    </GroupsContext.Provider>
  );
};

export const useGroupsContext = () => useContext(GroupsContext);
